import './style.scss'
import { Modal } from 'antd'
import { Buffer } from 'buffer'
import { FC, memo, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as homeApi from '@/api/home'
import Empty from '@/assets/empty.png'
import IconAdd from '@/assets/icon-add.png'
import ModelBase from '@/assets/model-base.png'
import ModelHigh from '@/assets/model-high.png'
import CheckLogin from '@/components/CheckLogin'
import { UserStore } from '@/global-states'
import { eventTracking, showPlanModal } from '@/libs/util'
import VoiceMarketBox from '../voice-market/components/VoiceMarketBox'
import CloneDrawer from './components/clone-drawer'
import CloneHighDrawer from './components/clone-high-drawer'
import VoiceBox from './components/VoiceBox'
import VoiceDetail from './components/VoiceDetail'

const Clone: FC = () => {
  const { t } = useTranslation(['voice', 'system'])
  const navigate = useNavigate()
  const { loginStatus } = UserStore
  const [globalTts, setGlobalTts] = useState<any[]>([])
  const [selfTts, setSelfTts] = useState<any[]>([])
  const [bookTts, setBookTts] = useState<any[]>()
  const [open, setOpen] = useState(false)
  const [cloneHighOpen, setCloneHighOpen] = useState(false)
  const [modelModal, setModelModal] = useState(false)
  const [detailGroup, setDetailGroup] = useState<any>()
  const voiceDetailRef = useRef<any>()
  const [activeIndex, setActiveIndex] = useState(0)
  const [model, setModel] = useState(0)

  useEffect(() => {
    eventTracking('VoiceClonePage')
    ;(window as any).Buffer = Buffer
    UserStore.updateUserPackage()
  }, [])

  useEffect(() => {
    getGlobalTts()
    getSelfTts()
    getBookTts()
  }, [loginStatus])

  const getSelfTts = async (toDetail?: boolean) => {
    const res = await homeApi.getSelfTtsGroups()
    setSelfTts(res?.list || [])
    if (toDetail) {
      setDetailGroup(res?.list?.[0])
    }
  }

  const getGlobalTts = async () => {
    const res = await homeApi.getGlobalTtsGroups()
    setGlobalTts((res?.list || []).map((l: any) => ({ ...l, global: true })))
  }

  const getBookTts = async () => {
    const res = await homeApi.getBookmarkedCommunityVoice()
    setBookTts(res?.list || [])
  }

  const cloneAudio = () => {
    eventTracking('InstantCloneBasicClick')
    setModelModal(false)
    setModel(0)
    flushSync(() => {
      setOpen(true)
    })
  }

  const cloneFishAudio = () => {
    eventTracking('InstantCloneFishClick')
    setModelModal(false)
    setModel(1)
    flushSync(() => {
      setOpen(true)
    })
  }

  const cloneHigAudio = async () => {
    eventTracking('InstantClonePremiumClick')
    setModelModal(false)
    setCloneHighOpen(true)
  }

  const onOk = () => {
    if (detailGroup?.id) {
      voiceDetailRef.current.getDetail()
    } else {
      getSelfTts(true)
    }
  }

  return (
    <div className="page-common page-clone">
      {detailGroup?.id ? (
        <VoiceDetail
          group={detailGroup}
          onBack={() => {
            setDetailGroup(undefined)
            getSelfTts()
            getBookTts()
          }}
          onAdd={() => {
            setModelModal(true)
          }}
          ref={voiceDetailRef}
          upgradePlan={() => showPlanModal()}
        />
      ) : (
        <>
          <div className="common-header">{t('voice.voice_clone')}</div>
          <div className="common-wrapper">
            <div className="clone-tabs">
              <div className={`tabs-item ${activeIndex === 0 ? 'actived' : ''}`} onClick={() => setActiveIndex(0)}>
                <span>{t('voice.my_voices')}</span>
              </div>
              <div className={`tabs-item ${activeIndex === 1 ? 'actived' : ''}`} onClick={() => setActiveIndex(1)}>
                <span>{t('voice.my_favorites')}</span>
              </div>
            </div>
            <div className="clone-wrapper">
              {activeIndex === 0 ? (
                <>
                  <div className="voice-box">
                    <CheckLogin>
                      <div
                        className="empty-group"
                        onClick={() => {
                          eventTracking('InstantCloneClick')
                          setModelModal(true)
                        }}
                      >
                        <img className="icon-add" src={IconAdd} />
                        <div className="text gradient">{t('voice.voice_clone')}</div>
                      </div>
                    </CheckLogin>
                  </div>

                  <VoiceBox
                    onItemClick={(t) => setDetailGroup(t)}
                    onUpdate={getSelfTts}
                    list={selfTts}
                    upgradePlan={() => showPlanModal()}
                  />
                </>
              ) : (
                <>
                  {bookTts?.length ? (
                    <VoiceMarketBox
                      onItemClick={(t) => setDetailGroup({ ...t, book: true })}
                      onDelete={getBookTts}
                      list={bookTts}
                    />
                  ) : (
                    <div className="data-empty">
                      <img className="empty" src={Empty} />
                      <p>
                        {t('voice.no_favorites')}
                        <span className="data-empty-link gradient" onClick={() => navigate('/market/voice')}>
                          {t('voice.voice_market')}
                        </span>
                        {t('voice.browse')}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="module-title">{t('voice.public_voice_library')}</div>
            <div className="clone-wrapper">
              <VoiceBox onItemClick={(t) => setDetailGroup({ ...t, global: true })} list={globalTts} />
            </div>
          </div>
        </>
      )}

      <CloneDrawer model={model} open={open} onCancel={() => setOpen(false)} onOk={onOk} group={detailGroup} />
      <CloneHighDrawer open={cloneHighOpen} onCancel={() => setCloneHighOpen(false)} onOk={onOk} group={detailGroup} />

      <Modal
        width={574}
        open={modelModal}
        title={t('voice.model_selection')}
        footer={null}
        onCancel={() => setModelModal(false)}
      >
        <div className="model-modal">
          <div className="model-item" onClick={cloneAudio}>
            <div className="left">
              <img src={ModelBase} />
            </div>
            <div className="center">
              <div className="title">{t('voice.basic_version')}</div>
              <div className="desc">{t('voice.basic_version_desc')}</div>
            </div>
            <div className="right recommond">
              <span className="gradient">{t('voice.recommended_choice')}</span>
            </div>
          </div>
          <div className="model-item" onClick={cloneHigAudio}>
            <div className="left">
              <img src={ModelHigh} />
            </div>
            <div className="center">
              <div className="title">{t('voice.high_fidelity')}</div>
              <div className="desc">{t('voice.high_fidelity_desc')}</div>
            </div>
            <div className="right high">
              <span className="gradient-yellow">{t('voice.exclusive')}</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default memo(Clone)
